/**
 * Phase 4 — Safety Observation detail. View fields/photos + close (approve-gated).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, CloudOff } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import AuthImage from '../../components/safety/AuthImage';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
import { formatDateTime as fmt } from '../../lib/format';

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800 whitespace-pre-wrap">{value || '—'}</div>
  </div>
);

export default function ObservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState('');
  const [closing, setClosing] = useState(false);

  const load = () => apiGet(`/safety/observations/${id}`).then(setDoc).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const close = async () => {
    setClosing(true);
    try {
      await apiPost(`/safety/observations/${id}/close`, { closure_notes: notes });
      toast.success('Observation closed');
      load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error('You do not have permission to close observations.');
      else toast.error(e?.response?.data?.detail || 'Failed to close');
    } finally {
      setClosing(false);
    }
  };

  if (!doc) return <div className="text-center text-xs text-slate-400 py-16 uppercase tracking-widest font-display">Loading…</div>;
  const isClosed = doc.status === 'closed' || doc.status === 'waived';

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/app/safety')} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3" data-testid="obs-back"><ArrowLeft size={14} /> Back to Safety</button>
      <PageHeader overline={doc.category} title="Safety Observation"
        actions={<span className={`tag border ${SEV_TONE[doc.severity]}`}>{doc.severity}</span>} />

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="observation-detail">
        {doc.offline_queued_at && (
          <div data-testid="obs-offline-audit" className="flex items-center gap-2 text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm">
            <CloudOff size={13} /> Filed offline on-site at <b>{fmt(doc.offline_queued_at)}</b> — synced later.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status" value={(doc.status || '').replace('_', ' ')} />
          <Field label="Location" value={doc.location_on_site} />
          <Field label="Raised By" value={`${doc.raised_by} · ${fmt(doc.raised_at)}`} />
          {isClosed && <Field label="Closed By" value={`${doc.closed_by} · ${fmt(doc.closed_at)}`} />}
        </div>
        <Field label="Description" value={doc.description} />
        {doc.immediate_action_taken && <Field label="Immediate Action Taken" value={doc.immediate_action_taken} />}
        {doc.recommendation && <Field label="Recommendation" value={doc.recommendation} />}

        {(doc.photos || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Photos ({doc.photos.length})</div>
            <div className="flex flex-wrap gap-2">
              {doc.photos.map((p) => (
                <a key={p.id} href={p.image_url} onClick={(e) => e.preventDefault()}>
                  <AuthImage src={p.image_url} alt="observation" className="w-28 h-28 object-cover rounded-sm border border-slate-200" />
                </a>
              ))}
            </div>
          </div>
        )}

        {isClosed ? (
          doc.closure_notes && <Field label="Closure Notes" value={doc.closure_notes} />
        ) : (
          <div className="border-t border-slate-200 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Close this observation</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Closure notes (what was done)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2" data-testid="obs-closure-notes" />
            <button onClick={close} disabled={closing} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="obs-close-btn">
              {closing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Close Observation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
